<template>
  <div class="full-report">
    <div class="assessment-logo-header">
      <no-drag-image src="@/assets/logo-regular.png" class="logo-small" />
    </div>
    <div class="assessment-header">
      <h1>{{ $t('report.headline') }}</h1>
      <div class="report-button-container">
        <app-button
          :disabled="downloading"
          class="btn-pdf-download"
          @click="downloadPdf"
        >
          {{ $t(downloading ? 'report.generatingReport' : 'report.buttonDownloadReport') }}
        </app-button>

        <app-button
          class="btn-send-report ms-2"
          @click="openSendDialog"
        >
          {{ $t('report.buttonSendReport') }}
        </app-button>
      </div>
    </div>
    <div class="summary-container">
      <markdown-text>{{ $t('report.intro') }}</markdown-text>
      <h1>1. {{ $t('report.introSummary') }}</h1>
      <summary-report
        :consideration-level="considerationLevel"
        :high-risk-indicator="highRiskIndicator"
        :bdd-indicator="bddIndicator"
        :impulsivity-indicator="impulsivityIndicator"
        :advices="advices"
        origin="fullReport"
      />
    </div>

    <h1 class="page-break-before">
      2. {{ $t('report.introPostSummary') }}
    </h1>

    <div v-if="considerationAdvice" class="consideration-container">
      <markdown-text>{{ considerationAdvice }}</markdown-text>
    </div>
    <div
      v-if="isConsiderationLevel(ConsiderationLevels.RED) || highRiskIndicator"
      class="high-risk-container mt-4"
    >
      <markdown-text>{{ $t('report.highRiskTrigger') }}</markdown-text>
    </div>
    <div v-if="bddIndicator" class="bdd-container mt-4">
      <markdown-text>{{ $t('report.bddTrigger') }}</markdown-text>
    </div>
    <div v-if="impulsivityIndicator" class="impulsivity-container mt-4">
      <markdown-text>{{ $t('report.impulsivenessTrigger') }}</markdown-text>
    </div>
    <div
      v-if="isConsiderationLevel([ConsiderationLevels.RED, ConsiderationLevels.YELLOW]) || bddIndicator"
    >
      <markdown-text>{{ $t('report.focusIllusion') }}</markdown-text>
    </div>

    <div v-if="advices.length > 0" class="other-advice-container mt-4">
      <p>{{ $t('reportSummary.otherAdvice') }}</p>
      <div
        v-for="adviceHeadline in adviceHeadlines" :key="adviceHeadline.headline"
        class="advice-head"
      >
        <h3>{{ adviceHeadline.headline }}</h3>

        <div v-for="advice in adviceHeadline.advice" :key="advice.id" class="other-advice">
          <p>{{ advice.advice }}</p>
        </div>
      </div>
    </div>

    <div class="end-text">
      <markdown-text>{{ $t('report.endText') }}</markdown-text>
    </div>

    <div v-if="referralForm" class="referral-form mb-3">
      <h3>{{ $t('report.appendixHeadline') }}</h3>
      <h4>{{ $t('report.referralFormHeadline') }}</h4>
      <markdown-text>{{ $t('report.referalFormText') }}</markdown-text>
    </div>

    <h1 class="page-break-before answers-headline">
      3. {{ $t('report.yourAnswersHeadline') }}
    </h1>

    <answers-given :question-answers="questionAnswers" />
  </div>
</template>

<script>

import { mapState } from 'pinia';
import { useSurveyStore } from '@/store';
import extend from 'extend';
import slugify from 'slugify';
import {
  CONSIDERATION_LEVEL_GREEN,
  CONSIDERATION_LEVEL_RED,
  CONSIDERATION_LEVEL_YELLOW,
} from '@/enum/ConsiderationLevel';
import NoDragImage from '@/components/common/NoDragImage';
import { STATIC_ANSWERS_DIAGNOSES, STATIC_ANSWERS_TREATMENTS } from '@/enum/StaticAnswers';
import MarkdownText from '@/components/common/MarkdownText';
import AppButton from '@/components/common/AppButton';
import PdfGeneratorMixin from '@/mixins/PdfGeneratorMixin';
import SummaryReport from '@/components/Report/SummaryReport';
import { formatDate } from '@/utils/dateHelper';
import AnswersGiven from '@/components/Report/AnswersGiven';
import Lightbox from '../common/Lightbox';

export default {
  name: 'FullReport',
  components: {
    AnswersGiven, SummaryReport, AppButton, NoDragImage, MarkdownText, Lightbox,
  },
  props: {
    considerationLevel: {
      type: String,
      required: true,
    },
    highRiskIndicator: {
      type: Boolean,
      required: true,
    },
    bddIndicator: {
      type: Boolean,
      required: true,
    },
    impulsivityIndicator: {
      type: Boolean,
      required: true,
    },
    referralForm: {
      type: Boolean,
      required: true,
    },
    advices: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    questionAnswers: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
    showDebugInfo: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['sendReport'],
  data() {
    return {
      adviceHeadlines: [],

      ConsiderationLevels: {
        GREEN: CONSIDERATION_LEVEL_GREEN,
        YELLOW: CONSIDERATION_LEVEL_YELLOW,
        RED: CONSIDERATION_LEVEL_RED,
      },

      downloading: false,
    };
  },
  computed: {
    ...mapState(useSurveyStore, ['questionGroups', 'treatmentCategories', 'diagnosisCategories']),
    considerationLevelString() {
      if (this.considerationLevel === CONSIDERATION_LEVEL_GREEN) {
        return 'Green';
      }

      if (this.considerationLevel === CONSIDERATION_LEVEL_YELLOW) {
        return 'Yellow';
      }

      if (this.considerationLevel === CONSIDERATION_LEVEL_RED) {
        return 'Red';
      }

      return 'Unknown';
    },
    considerationAdvice() {
      if (this.considerationLevel === CONSIDERATION_LEVEL_GREEN) {
        return null;
      }

      return this.$t(`report.consideration${this.considerationLevelString}`);
    },
  },
  mixins: [
    PdfGeneratorMixin,
  ],
  watch: {
    questionAnswers: {
      handler() {
        this.recalculateAdviceHeadlines();
      },
      deep: true,
    },
  },
  mounted() {
    this.recalculateAdviceHeadlines();
  },
  methods: {
    isConsiderationLevel(considerationLevels) {
      if (considerationLevels instanceof Array) {
        return considerationLevels.includes(this.considerationLevel);
      }
      return this.considerationLevel === considerationLevels;
    },
    openSendDialog() {
      this.$emit('sendReport');
    },
    sendReport() {
      this.$apiClient.report.sendReport(this.sendEmailAddress);
    },
    downloadPdf() {
      this.downloading = true;

      const dateTime = slugify(formatDate(new Date()));

      const filename = `full-report-${dateTime}.pdf`;

      this.downloadPdfFromBody(filename)
        .then(() => {
          this.downloading = false;
        })
        .catch((error) => {
          this.downloading = false;

          this.$snackbar.add({
            type: 'error',
            text: error,
          });
        });
    },
    recalculateAdviceHeadlines() {
      const adviceHeadlines = {};

      for (const advice of this.advices) {
        if (!Object.keys(adviceHeadlines).includes(advice.headline)) {
          adviceHeadlines[advice.headline] = {
            headline: advice.headline,
            advice: [],
          };
        }

        adviceHeadlines[advice.headline].advice.push({
          advice: advice.advice,
          id: advice.id,
        });
      }

      this.adviceHeadlines = Object.values(adviceHeadlines);
    },
  },
};
</script>

<style lang="scss">
@media print {
  .full-report {
    background: transparent !important;
    padding-bottom: 0;

    hr {
      margin-top: 5px;
      margin-bottom: 5px;
    }

    h1, h2 {
      page-break-inside: avoid;
      page-break-after: avoid;
    }

    .new-page {
      page-break-before: always;
    }

    .advice-head {
      h2 {
        break-inside: avoid;
        //break-after: avoid;

        &::after {
          content: "";
          display: block;
          height: 150px;
          margin-bottom: -150px;
        }
      }
    }

    .other-advice {
      break-inside: avoid;
      white-space: pre-line; // To fix newlines for eg. commercial tourism
    }

    .report-button-container {
      display: none !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.referral-form {
  page-break-before: always;
  page-break-inside: avoid;
  page-break-after: always;
}

h1 {
  &.page-break-before {
    page-break-before: always !important;
    page-break-after: avoid !important;
    page-break-inside: avoid !important;
  }
}

.assessment-logo-header {
  padding: 0 60px;
  margin-top: 20px;
  margin-bottom: 10px;

  img {
    max-width: 100%;
  }
}

.logo-small {
  max-width: 50vw;
}

.full-report {
  color: #063C5A;
  background: #F8F8F8;
}

.summary-container {
  margin-top: 60px;

  p {
    margin-top: 20px;
    background: #EDF1F4;
    border: 2px solid #557F97;
    border-radius: 5px;
    padding: 10px;
    font-weight: 700;
    font-size: 14pt;
  }
}

.icon {
  text-align: center;

  img {
    max-height: 50px;
  }
}

p {
  font-size: 12pt;
  color: #063C5A;
  font-weight: 600;
}

hr {
  opacity: 1;
  background: #EDF1F4;
  height: 5px;
}

.full-report {
  padding: 0 15px 80px 15px;
  background: #F8F8F8;
}

.assessment-header {
  .report-button-container {
    position: absolute;
    top: 20px;
    right: 0;
  }

  position: relative;
}

ul {
  list-style-type: circle;
}

@media (max-width: 767.98px) {
  .report-button-container {
    position: relative !important;
    top: auto !important;
    right: auto !important;
    margin-top: 10px !important;
  }

  .summary-container {
    margin-top: 10px !important;
  }
}

@media (max-width: 480px) {
  .report-button-container {
    .app-button {
      margin-left: 0 !important;
      margin-top: 10px;
      width: 100%;
      display: block;
    }
  }
}
</style>
